import { useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { PortalRenderer } from '@/components/PortalRenderer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { TimePicker } from '@/components/ui/time-picker';
import { useConfigStore } from '@/store/ConfigStore';
import { useSearchStore } from '@/store/SearchStore';

const TODAY_LABEL = dayjs(new Date()).format('dddd, D MMMM');
const TOMORROW_LABEL = dayjs(new Date()).add(1, 'day').format('dddd, D MMMM');
const AFTER_TOMORROW_LABEL = dayjs(new Date()).add(2, 'day').format('dddd, D MMMM');

export const TravelTime = () => {
  const portalSelector = useConfigStore((state) => state.portals?.['travel-time']);
  return (
    <PortalRenderer portalSelector={portalSelector}>
      <TravelTimeInternal />
    </PortalRenderer>
  );
};

const TravelTimeInternal = () => {
  const { travelTime, setTravelTime, searching } = useSearchStore();

  const [date, setDate] = useState<Date>(travelTime || new Date());
  const [time, setTime] = useState<Date>(travelTime || new Date());

  const [radioValue, setRadioValue] = useState<'0' | '1' | '2'>('0');
  const [timeValue, setTimeValue] = useState<Date>(travelTime || new Date());

  const [formattedDate, setFormattedDate] = useState<string>('Leave Now');

  const refreshDefaultRadioValue = () => {
    const now = dayjs();
    const travel = dayjs(travelTime);
    let newVal: '0' | '1' | '2' = '0';
    if (!travelTime || travel.isSame(now, 'minute') || travel.isBefore(now)) {
      newVal = '0';
    } else if (travel.isSame(now.add(1, 'day'), 'day')) {
      newVal = '1';
    } else if (travel.isSame(now.add(2, 'day'), 'day')) {
      newVal = '2';
    }
    setRadioValue(newVal);
  };

  useEffect(() => {
    const dateTime = new Date(date);
    dateTime.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
    const isDateTimeNow = dayjs(dateTime).isSame(new Date(), 'minute') || dayjs(dateTime).isBefore(new Date());
    setTravelTime(isDateTimeNow ? undefined : dateTime);
    setFormattedDate(isDateTimeNow ? 'Leave now' : dayjs(dateTime).format('D MMM, HH:mm'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, time]);

  useEffect(() => {
    setFormattedDate(!travelTime ? 'Leave now' : dayjs(travelTime).format('MMM D, HH:mm'));
  }, [travelTime]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={searching}>
          <ClockIcon /> {formattedDate}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xs" onOpenAutoFocus={refreshDefaultRadioValue}>
        <DialogHeader>
          <DialogTitle>Set date and time</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Label className="font-semibold">
            Date
            <RadioGroup value={radioValue} onValueChange={(value) => setRadioValue(value as '0')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="0" id="option-today" />
                <Label htmlFor="option-today">
                  Today
                  <span className="font-light dark:opacity-50">{TODAY_LABEL}</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="option-tomorrow" />
                <Label htmlFor="option-tomorrow">
                  Tomorrow
                  <span className="font-light dark:opacity-50">{TOMORROW_LABEL}</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="2" id="option-after-tomorrow" />
                <Label htmlFor="option-after-tomorrow">
                  Day after tomorrow
                  <span className="font-light dark:opacity-50">{AFTER_TOMORROW_LABEL}</span>
                </Label>
              </div>
            </RadioGroup>
          </Label>
          <Label className="font-semibold">
            Time
            <TimePicker onSelect={setTimeValue} defaultTime={time} />
          </Label>
        </div>
        <DialogFooter>
          <DialogClose className="w-full">
            <Button
              asChild
              type="submit"
              className="w-full"
              onClick={() => {
                setDate(dayjs(new Date()).add(Number(radioValue), 'day').toDate());
                setTime(timeValue);
              }}
            >
              <div>Set</div>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ClockIcon = () => (
  <svg width="20" height="21" stroke="currentColor" viewBox="0 0 20 21" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 2.375C8.39303 2.375 6.82214 2.85152 5.486 3.74431C4.14985 4.6371 3.10844 5.90605 2.49348 7.3907C1.87852 8.87535 1.71762 10.509 2.03112 12.0851C2.34463 13.6612 3.11846 15.1089 4.25476 16.2452C5.39106 17.3815 6.8388 18.1554 8.4149 18.4689C9.99099 18.7824 11.6247 18.6215 13.1093 18.0065C14.594 17.3916 15.8629 16.3502 16.7557 15.014C17.6485 13.6779 18.125 12.107 18.125 10.5C18.1227 8.34581 17.266 6.28051 15.7427 4.75727C14.2195 3.23403 12.1542 2.37727 10 2.375ZM10 17.375C8.64026 17.375 7.31105 16.9718 6.18046 16.2164C5.04987 15.4609 4.16868 14.3872 3.64833 13.1309C3.12798 11.8747 2.99183 10.4924 3.2571 9.15875C3.52238 7.82513 4.17716 6.60013 5.13864 5.63864C6.10013 4.67716 7.32514 4.02237 8.65876 3.7571C9.99238 3.49183 11.3747 3.62798 12.631 4.14833C13.8872 4.66868 14.9609 5.54987 15.7164 6.68045C16.4718 7.81104 16.875 9.14025 16.875 10.5C16.8729 12.3227 16.1479 14.0702 14.8591 15.3591C13.5702 16.6479 11.8227 17.3729 10 17.375ZM15 10.5C15 10.6658 14.9342 10.8247 14.8169 10.9419C14.6997 11.0592 14.5408 11.125 14.375 11.125H10C9.83424 11.125 9.67527 11.0592 9.55806 10.9419C9.44085 10.8247 9.375 10.6658 9.375 10.5V6.125C9.375 5.95924 9.44085 5.80027 9.55806 5.68306C9.67527 5.56585 9.83424 5.5 10 5.5C10.1658 5.5 10.3247 5.56585 10.4419 5.68306C10.5592 5.80027 10.625 5.95924 10.625 6.125V9.875H14.375C14.5408 9.875 14.6997 9.94085 14.8169 10.0581C14.9342 10.1753 15 10.3342 15 10.5Z" />
  </svg>
);
